import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, MapPin, Stethoscope, Star } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api, imgUrl } from "@/lib/api";
import PatientNav from "@/components/PatientNav";

const FALLBACK =
  "https://images.unsplash.com/photo-1594824476967-48c8b964273f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwzfHxkb2N0b3IlMjBwb3J0cmFpdCUyMHByb2Zlc3Npb25hbHxlbnwwfHx8fDE3ODc2MTg4MjV8MA&ixlib=rb-4.1.0&q=85";

export default function PatientHome() {
  const [q, setQ] = useState("");
  const [specialty, setSpecialty] = useState("");
  const [specs, setSpecs] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (specialty) params.specialty = specialty;
    const { data } = await api.get("/doctors", { params });
    setDoctors(data);
    setLoading(false);
  };

  useEffect(() => {
    api.get("/doctors/specialties").then((r) => setSpecs(r.data));
  }, []);

  // Predictive search: reload as the user types (debounced), ignoring stale responses
  useEffect(() => {
    let active = true;
    setLoading(true);
    const t = setTimeout(async () => {
      const params = {};
      if (q) params.q = q;
      if (specialty) params.specialty = specialty;
      try {
        const { data } = await api.get("/doctors", { params });
        if (active) setDoctors(data);
      } finally {
        if (active) setLoading(false);
      }
    }, q ? 300 : 0);
    return () => { active = false; clearTimeout(t); };
  }, [q, specialty]);

  return (
    <div className="min-h-screen bg-slate-50">
      <PatientNav />
      <div className="mx-auto max-w-6xl px-6 py-8">
        <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">Encuentra tu médico</h1>
        <p className="mt-1 text-sm font-semibold text-slate-500">Busca por nombre, especialidad o centro médico.</p>

        <div className="mt-5 flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="Nombre, especialidad o centro…"
              className="rounded-full border-[#E2E8F0] pl-10"
              data-testid="doctor-search-input"
            />
          </div>
          <Button onClick={load} className="rounded-full bg-[#0284C7] font-bold hover:bg-[#0369a1]" data-testid="search-btn">
            Buscar
          </Button>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <button
            onClick={() => setSpecialty("")}
            className={`rounded-full border px-4 py-1.5 text-xs font-bold ${specialty === "" ? "border-[#0284C7] bg-sky-50 text-[#0284C7]" : "border-[#E2E8F0] text-slate-500"}`}
            data-testid="spec-all"
          >
            Todas
          </button>
          {specs.map((s) => (
            <button
              key={s}
              onClick={() => setSpecialty(s)}
              className={`rounded-full border px-4 py-1.5 text-xs font-bold ${specialty === s ? "border-[#0284C7] bg-sky-50 text-[#0284C7]" : "border-[#E2E8F0] text-slate-500"}`}
              data-testid={`spec-${s}`}
            >
              {s}
            </button>
          ))}
        </div>

        <div className="mt-8 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {loading && <p className="text-sm font-semibold text-slate-400">Cargando médicos…</p>}
          {!loading && doctors.length === 0 && (
            <div className="col-span-full rounded-2xl border border-dashed border-[#E2E8F0] bg-white p-10 text-center">
              <Stethoscope className="mx-auto h-8 w-8 text-slate-300" />
              <p className="mt-3 text-sm font-bold text-slate-500">No hay médicos disponibles todavía.</p>
            </div>
          )}
          {doctors.map((d, i) => (
            <Link
              key={d.user_id}
              to={`/medico/${d.user_id}`}
              className="group animate-fade-in-up rounded-2xl border border-[#E2E8F0] bg-white p-5 transition-all duration-200 hover:shadow-sm"
              style={{ animationDelay: `${i * 40}ms` }}
              data-testid={`doctor-card-${d.user_id}`}
            >
              <div className="flex items-center gap-4">
                <img
                  src={imgUrl(d.picture || d.profile?.photo_url) || FALLBACK}
                  alt={d.name}
                  className="h-16 w-16 rounded-full border border-[#E2E8F0] object-cover"
                  onError={(e) => { e.currentTarget.src = FALLBACK; }}
                />
                <div className="min-w-0">
                  <h3 className="truncate text-lg font-extrabold tracking-tight text-slate-900">{d.name}</h3>
                  <p className="text-sm font-bold text-[#0284C7]">{d.profile?.specialty}</p>
                  {d.license_status === "approved" && (
                    <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-600" data-testid={`verified-${d.user_id}`}>✓ Verificado</span>
                  )}
                </div>
              </div>
              <div className="mt-4 space-y-1.5 text-sm font-semibold text-slate-500">
                <div className="flex items-center gap-1.5"><MapPin className="h-4 w-4 text-slate-400" /> {d.profile?.center_name || d.profile?.location}</div>
                <div className="flex items-center gap-1.5"><Star className="h-4 w-4 text-amber-400" /> RD$ {d.profile?.price?.toLocaleString?.() ?? d.profile?.price}</div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
